// A `.stickyTop` section is pinned by taking the section itself out of the flow and leaving a
// same-sized placeholder behind to hold its space.
//
// This used to append a `clone(true, true)` of the section and run `CF2Component.hydrateTree`
// over the copy. Hydrating re-runs `initialize()` and `mount()` on every CF2 element in the
// subtree, so anything stateful mounted twice: duplicate DOM ids (`getElementById` resolves to
// the original, not the copy the visitor is looking at), duplicate fetches, and duplicate
// listeners layered on top of the jQuery handlers `clone(true, true)` had already copied over.
// Checkout broke outright — the second `StripeCheckoutLogic` aborted the first one's
// `stripe_get_session` through the singleton `stripeSessionApiFetcher`, and the instance left
// on the page once the copy was removed was the one rendering `0x20` with no card fields, so
// the buyer could not pay (ENG-7676).
//
// Pinning the original keeps it at one mount, and the section carries whatever live state it
// had straight into the pinned position.
//
// TODO: Ideally we want this functionality implemented using IntersectionObserver

// The name is a leftover: this used to be the class on the appended copy. It is kept because
// published pages can carry custom CSS that targets it.
const STUCK_CLASS = 'stickyClone'

function createPlaceholder(): JQuery {
  return $('<div>').addClass('stickyPlaceholder').attr('aria-hidden', 'true')
}

// The placeholder stands in for the section's whole margin box, so the content below does not
// jump when the section leaves the flow, and so the offset we unpin at survives a reflow of
// whatever sits above the section. Returns the height it reserved: the section's in-flow height.
function sizePlaceholder(sticky: JQuery, placeholder: JQuery): number {
  const height = sticky.outerHeight() ?? 0
  placeholder.css({
    boxSizing: 'border-box',
    height,
    marginTop: sticky.css('marginTop'),
    marginBottom: sticky.css('marginBottom'),
  })
  return height
}

$(document).ready(function () {
  const $stickyElements = $('.stickyTop')

  $stickyElements.each((_index, element) => {
    const sticky = $(element)
    const placeholder = createPlaceholder()
    let stuck = false

    // The height the section had in the flow. `position: fixed` takes a pinned section out of
    // its parent's flex layout, so a section the parent stretched (Fill Available Height) shrinks
    // to its content height the moment it is pinned. The placeholder must keep the flow height,
    // or the page below jumps up.
    let flowHeight = 0

    // A section hidden on this device (Desktop-Only / Mobile-Only, `display: none`) has no box.
    // `offset()` reads 0 for it while the placeholder reports a real position, so the pin and
    // unpin thresholds could never agree and the section would pin and unpin on every scroll.
    const hasBox = () => element.getClientRects().length > 0

    // A pinned section's contents can still grow on their own — a Checkout gets taller as
    // Stripe's card fields mount — and its width is no longer bound by its parent's. Keep the
    // placeholder in step, but never below the flow height.
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (stuck) placeholder.css('height', Math.max(flowHeight, sticky.outerHeight() ?? 0))
          })

    const pin = () => {
      flowHeight = sizePlaceholder(sticky, placeholder)
      placeholder.insertBefore(sticky)
      sticky.addClass(STUCK_CLASS)
      stuck = true
      resizeObserver?.observe(element)
    }

    const unpin = () => {
      resizeObserver?.unobserve(element)
      sticky.removeClass(STUCK_CLASS)
      placeholder.detach()
      stuck = false
    }

    // While pinned the section's own offset tracks the viewport rather than the document, so
    // it can no longer say where it belongs. The placeholder, sitting exactly where it was,
    // can — and it keeps saying so correctly if the page reflows underneath us.
    const stickyTop = () => (stuck ? placeholder.offset().top : sticky.offset().top)

    // Initial check to see if page is already scrolled when loaded
    if (hasBox() && $(window).scrollTop() > stickyTop()) {
      pin()
    }

    document.addEventListener('scroll', () => {
      const scrollTop = $(window).scrollTop()

      if (!stuck && hasBox() && scrollTop >= stickyTop()) {
        pin()
      } else if (stuck && scrollTop < stickyTop()) {
        unpin()
      }
    })
  })
})
